export type VariantPricingProduct = {
  price?: number | string | null
  originalPrice?: number | string | null
  discountedPrice?: number | string | null
  discount?: number | string | null
  campaignDiscountOverridePercent?: number | string | null
  isSale?: boolean | null
}

export type VariantPricingVariant = {
  price?: number | string | null
}

export type ResolvedVariantPricing = {
  basePrice: number
  finalPrice: number
  hasDiscount: boolean
  isSale: boolean
  discountPercent?: number
}

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value
  }

  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, value))

const roundCurrencyAmount = (value: number): number =>
  Math.round((Number(value) || 0) * 100) / 100

const computeDiscountedPrice = (price: number, percentOff: number): number => {
  const normalizedPercent = clampPercent(Number(percentOff) || 0)
  const normalizedPrice = Math.max(0, Number(price) || 0)
  const discounted = normalizedPrice * (1 - normalizedPercent / 100)
  return roundCurrencyAmount(Math.max(0, discounted))
}

const normalizeManualDiscountedPrice = (
  value: unknown,
  basePrice: number
): number | null => {
  const normalizedBase = Number(basePrice)
  const candidate = toFiniteNumber(value)

  if (candidate === null) return null
  if (!Number.isFinite(normalizedBase) || normalizedBase <= 0) return null
  if (candidate <= 0 || candidate >= normalizedBase) return null
  return roundCurrencyAmount(candidate)
}

const resolveProductBasePrice = (
  product?: VariantPricingProduct | null
): number => {
  const originalPrice = toFiniteNumber(product?.originalPrice)
  if (originalPrice !== null && originalPrice >= 0) {
    return originalPrice
  }

  const basePrice = toFiniteNumber(product?.price)
  if (basePrice !== null && basePrice >= 0) {
    return basePrice
  }

  const discountedPrice = toFiniteNumber(product?.discountedPrice)
  if (discountedPrice !== null && discountedPrice >= 0) {
    return discountedPrice
  }

  return 0
}

const resolveVariantBasePrice = ({
  product,
  variant,
}: {
  product?: VariantPricingProduct | null
  variant?: VariantPricingVariant | null
}): number => {
  const variantPrice = toFiniteNumber(variant?.price)
  if (variantPrice !== null) {
    return Math.max(0, variantPrice)
  }

  return resolveProductBasePrice(product)
}

const resolveDiscountPolicy = (
  product?: VariantPricingProduct | null
): {
  percent: number | null
} => {
  const campaignPercent = toFiniteNumber(
    product?.campaignDiscountOverridePercent
  )
  if (campaignPercent !== null && campaignPercent > 0) {
    return {
      percent: clampPercent(campaignPercent),
    }
  }

  const catalogPercent = toFiniteNumber(product?.discount)
  if (catalogPercent !== null && catalogPercent > 0) {
    return {
      percent: clampPercent(catalogPercent),
    }
  }

  if (product?.isSale === false) {
    const productBasePrice = resolveProductBasePrice(product)
    const projectedDiscountedPrice = normalizeManualDiscountedPrice(
      product?.discountedPrice,
      productBasePrice
    )
    if (projectedDiscountedPrice !== null && productBasePrice > 0) {
      const derivedPercent =
        ((productBasePrice - projectedDiscountedPrice) / productBasePrice) * 100
      if (Number.isFinite(derivedPercent) && derivedPercent > 0) {
        return { percent: clampPercent(derivedPercent) }
      }
    }
  }

  return {
    percent: null,
  }
}

export const resolveVariantPricing = ({
  product,
  variant,
}: {
  product?: VariantPricingProduct | null
  variant?: VariantPricingVariant | null
}): ResolvedVariantPricing => {
  const basePrice = resolveVariantBasePrice({ product, variant })
  const productBasePrice = resolveProductBasePrice(product)
  const manualSalePrice = normalizeManualDiscountedPrice(
    product?.discountedPrice,
    productBasePrice
  )
  const hasDeclaredPercent =
    (toFiniteNumber(product?.campaignDiscountOverridePercent) ?? 0) > 0 ||
    (toFiniteNumber(product?.discount) ?? 0) > 0
  const shouldUseManualSale =
    manualSalePrice !== null &&
    (product?.isSale === true ||
      (product?.isSale !== false && !hasDeclaredPercent))

  if (shouldUseManualSale) {
    return {
      basePrice,
      finalPrice: manualSalePrice,
      hasDiscount: manualSalePrice < basePrice,
      isSale: true,
      discountPercent: undefined,
    }
  }

  const discountPolicy = resolveDiscountPolicy(product)

  if (discountPolicy.percent !== null) {
    const discountedPrice = computeDiscountedPrice(
      basePrice,
      discountPolicy.percent
    )

    return {
      basePrice,
      finalPrice: discountedPrice,
      hasDiscount: discountedPrice < basePrice,
      isSale: false,
      discountPercent: discountPolicy.percent,
    }
  }

  return {
    basePrice,
    finalPrice: basePrice,
    hasDiscount: false,
    isSale: false,
    discountPercent: undefined,
  }
}
